import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { Application, CanvasSource, Sprite, Texture } from 'pixi.js';
import { acquireVideo, canonicalVideoKey, releaseVideo, whenVideoCached } from './videoPreloader';
import { rememberVideoPoster } from './videoPosterCache';

interface TimelineVideoBackground {
  src: string;
  startTime?: number;
  sourceType?: 'image' | 'video';
  mimeType?: string;
}

const getBackgroundSource = (image: string | TimelineVideoBackground) =>
  typeof image === 'string' ? image : image.src;

const getBackgroundStartTime = (image: string | TimelineVideoBackground) =>
  typeof image === 'string' ? 0 : image.startTime || 0;

export const VIDEO_BACKGROUND_MARKER = 'pixle-video-background=1';

const VIDEO_URL_REGEX = /\.(mp4|webm|mov|m4v|avi)(\?|#|$)/i;

export const isVideoSource = (
  image: string | TimelineVideoBackground,
  url: string
): boolean => {
  if (typeof image !== 'string') {
    if (image.sourceType === 'video') return true;
    if (image.sourceType === 'image') return false;
    if (image.mimeType?.startsWith('video/')) return true;
  }

  return VIDEO_URL_REGEX.test(url) || /^data:video\//i.test(url);
};

// First-frame waits are stall-based, not fixed: a stream that keeps moving
// never fails, however slow the link. Each wait gets a stall budget (no
// observable progress → reject) plus an absolute cap. Local blobs resolve
// near-instantly so they keep a tight cap; remote URLs get room for slow
// links and moov-at-end tail-chasing.
const STALL_BUDGET_MS = 15000;
const REMOTE_ABSOLUTE_CAP_MS = 120000;
const LOCAL_ABSOLUTE_CAP_MS = 15000;
const STALL_POLL_MS = 2000;

/** Test hook: shrink stall timings in unit tests. Never touch in production. */
export const videoWaitTuning = {
  stallBudgetMs: STALL_BUDGET_MS,
  pollMs: STALL_POLL_MS,
};

const isLocalMediaUrl = (url: string): boolean => /^(blob:|data:)/i.test(url);

const bufferedSeconds = (video: HTMLVideoElement): number => {
  try {
    const buffered = video.buffered;
    return buffered.length ? buffered.end(buffered.length - 1) : 0;
  } catch {
    return 0;
  }
};

const waitForVideoEvent = (
  video: HTMLVideoElement,
  eventName: keyof HTMLMediaElementEventMap,
  signal?: AbortSignal,
  absoluteCapMs?: number
) =>
  new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();
    const cap = absoluteCapMs ?? (isLocalMediaUrl(video.currentSrc || video.src)
      ? LOCAL_ABSOLUTE_CAP_MS
      : REMOTE_ABSOLUTE_CAP_MS);
    let lastProgressAt = Date.now();
    let lastReadyState = video.readyState;
    let lastBuffered = bufferedSeconds(video);
    let timer = 0;

    const observeProgress = () => {
      const readyState = video.readyState;
      const buffered = bufferedSeconds(video);
      if (readyState !== lastReadyState || buffered !== lastBuffered) {
        lastReadyState = readyState;
        lastBuffered = buffered;
        lastProgressAt = Date.now();
      }
    };
    const handleProgress = () => {
      lastProgressAt = Date.now();
    };
    const fail = (error: Error) => {
      cleanup();
      reject(error);
    };
    const handleEvent = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(video.error || new Error(`Video failed while waiting for ${eventName}`));
    };
    const handleAbort = () => {
      cleanup();
      reject(new DOMException('Video load cancelled', 'AbortError'));
    };
    const arm = () => {
      timer = window.setTimeout(() => {
        observeProgress();
        const now = Date.now();
        const readyState = video.readyState;
        const networkState = video.networkState;
        if (now - startedAt >= cap) {
          fail(new Error(
            `Video did not reach ${eventName} within ${Math.round(cap / 1000)}s ` +
            `(readyState=${readyState}, networkState=${networkState}, buffered=${lastBuffered.toFixed(1)}s)`
          ));
          return;
        }
        if (now - lastProgressAt >= videoWaitTuning.stallBudgetMs) {
          fail(new Error(
            `Video stalled while waiting for ${eventName}: no progress for ${Math.round(videoWaitTuning.stallBudgetMs / 1000)}s ` +
            `(readyState=${readyState}, networkState=${networkState}, buffered=${lastBuffered.toFixed(1)}s)`
          ));
          return;
        }
        arm();
      }, videoWaitTuning.pollMs);
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      video.removeEventListener(eventName, handleEvent);
      video.removeEventListener('error', handleError);
      video.removeEventListener('progress', handleProgress);
    };

    if (signal?.aborted) return handleAbort();
    if (video.error) return handleError();
    signal?.addEventListener('abort', handleAbort, { once: true });
    video.addEventListener(eventName, handleEvent, { once: true });
    video.addEventListener('error', handleError, { once: true });
    video.addEventListener('progress', handleProgress);
    arm();
  });

const waitForVideoFrame = async (video: HTMLVideoElement, signal?: AbortSignal) => {
  if (!video.videoWidth || !video.videoHeight) {
    await waitForVideoEvent(video, 'loadedmetadata', signal);
  }

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await waitForVideoEvent(video, 'loadeddata', signal);
  }
};

const shortVideoName = (url: string): string =>
  url.split(/[?#]/)[0].split('/').pop() || url.split(/[?#]/)[0];

/**
 * Remember the just-drawn first frame as a poster for instant revisits.
 * Best-effort: tainted canvases (CORS) and missing APIs skip silently — the
 * video itself is unaffected, only the future placeholder is lost.
 */
const rememberFirstFrame = (imageUrl: string, frameCanvas: HTMLCanvasElement): void => {
  try {
    if (typeof frameCanvas.toDataURL !== 'function') return;
    if (!frameCanvas.width || !frameCanvas.height) return;
    const dataUrl = frameCanvas.toDataURL('image/jpeg', 0.55);
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
      rememberVideoPoster(canonicalVideoKey(imageUrl), dataUrl);
    }
  } catch {
    // Tainted canvas or encoder unavailable: no poster this time.
  }
};

const formatMediaError = (video: HTMLVideoElement) => {  const error = video.error;
  const codeMap: Record<number, string> = {
    1: 'MEDIA_ERR_ABORTED',
    2: 'MEDIA_ERR_NETWORK',
    3: 'MEDIA_ERR_DECODE',
    4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
  };

  return {
    code: error?.code,
    codeName: error?.code ? codeMap[error.code] ?? 'UNKNOWN_MEDIA_ERROR' : undefined,
    message: error?.message,
    currentSrc: video.currentSrc || video.src,
    networkState: video.networkState,
    readyState: video.readyState,
  };
};

export const renderImage = async (
  app: Application | null,
  image: string | TimelineVideoBackground,
  width: number,
  height: number,
  timelineState?: React.MutableRefObject<TimelineState | null>,
  signal?: AbortSignal
): Promise<Sprite | undefined> => {
  if (!app) return;

  let cleanupVideo: (() => void) | undefined;
  try {
    signal?.throwIfAborted();
    const imageUrl = getBackgroundSource(image);
    const backgroundStartTime = getBackgroundStartTime(image);
    const isVideo = isVideoSource(image, imageUrl);

    // NOTE: the previous sprite is intentionally left alone here. The caller
    // (ImageRenderer) keeps it staged until this replacement's first frame
    // is drawn, so scene transitions hold the last frame instead of flashing
    // a white canvas while the next video loads.

    let sprite: Sprite;

    if (isVideo) {
      const video = await acquireVideo(imageUrl, signal);
      let disposed = false;
      cleanupVideo = () => {
        disposed = true;
        releaseVideo(imageUrl, video);
      };

      const handleVideoError = () => {
        console.error('Video background failed to load', formatMediaError(video));
      };

      const syncVideoToTimeline = (
        timelineTime?: number,
        forceSeek: boolean = false
      ) => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        const engineTime =
          typeof timelineTime === 'number'
            ? timelineTime
            : timelineState?.current?.getTime?.() || 0;
        const nextVideoTime = Math.max(0, engineTime - backgroundStartTime);
        const clampedVideoTime = Math.min(nextVideoTime, video.duration);

        if (
          forceSeek ||
          !attachedEngine?.isPlaying
        ) {
          if (Math.abs(video.currentTime - clampedVideoTime) > 0.08) {
            video.currentTime = clampedVideoTime;
          }
        }
      };

      const syncVideoRate = () => {
        if (!attachedEngine) return;
        video.playbackRate = attachedEngine.getPlayRate?.() ?? 1;
      };

      const playVideo = () => {
        syncVideoRate();
        syncVideoToTimeline(undefined, true);
        if (disposed) return;
        video
          .play()
          .then(scheduleDraw)
          .catch(() => undefined);
      };
      const pauseVideo = () => {
        video.pause();
        cancelScheduledDraw();
      };
      const handleTimeUpdate = ({ time }: { time: number }) =>
        syncVideoToTimeline(time, true);
      const handleRateChange = ({ rate }: { rate: number }) => {
        video.playbackRate = rate;
      };
      let attachedEngine: TimelineState | null = null;
      let attachTimelineInterval: number | undefined;

      const attachTimelineControls = (engine: TimelineState | null) => {
        if (!engine || attachedEngine === engine) return;

        attachedEngine = engine;
        syncVideoRate();
        engine.listener.on('play', playVideo);
        engine.listener.on('paused', pauseVideo);
        engine.listener.on('afterSetTime', handleTimeUpdate);
        engine.listener.on('afterSetPlayRate', handleRateChange);

        if (engine.isPlaying) {
          playVideo();
        } else {
          syncVideoToTimeline();
          pauseVideo();
        }
      };

      video.addEventListener('error', handleVideoError);
      const releaseLoadingVideo = cleanupVideo;
      cleanupVideo = () => {
        video.removeEventListener('error', handleVideoError);
        releaseLoadingVideo();
      };
      try {
        await waitForVideoFrame(video, signal);
      } catch (frameError) {
        // Progressive metadata wait stalled out (slow link, moov-at-end tail
        // chase). Fall back to the fully-downloaded blob once the background
        // fetch warms it, instead of giving up on the scene. Aborts and
        // already-local sources propagate immediately.
        if (
          signal?.aborted ||
          (frameError as Error)?.name === 'AbortError' ||
          /^(blob:|data:)/i.test(video.src)
        ) {
          throw frameError;
        }
        signal?.throwIfAborted();
        const objectUrl = await whenVideoCached(imageUrl, signal);
        signal?.throwIfAborted();
        if (!objectUrl || video.src === objectUrl) throw frameError;
        console.info(
          `[renderImage] Progressive load stalled for ${shortVideoName(imageUrl)}; ` +
          `retrying from warmed blob (${(frameError as Error)?.message})`
        );
        video.src = objectUrl;
        video.load();
        await waitForVideoFrame(video, signal);
      }
      signal?.throwIfAborted();

      const frameCanvas = document.createElement('canvas');
      const frameContext = frameCanvas.getContext('2d');

      if (!frameContext) {
        throw new Error('Unable to create video frame canvas');
      }

      frameCanvas.width = video.videoWidth;
      frameCanvas.height = video.videoHeight;

      const canvasSource = new CanvasSource({
        resource: frameCanvas,
        width: frameCanvas.width,
        height: frameCanvas.height,
      });

      const drawVideoFrame = () => {
        if (
          video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
          !video.videoWidth ||
          !video.videoHeight
        ) {
          return;
        }

        if (
          frameCanvas.width !== video.videoWidth ||
          frameCanvas.height !== video.videoHeight
        ) {
          frameCanvas.width = video.videoWidth;
          frameCanvas.height = video.videoHeight;
          canvasSource.resize(video.videoWidth, video.videoHeight);
        }

        frameContext.drawImage(video, 0, 0, frameCanvas.width, frameCanvas.height);
        canvasSource.update();
      };

      let animationFrameId: number | null = null;
      let videoFrameCallbackId: number | null = null;

      const scheduleDraw = () => {
        if (disposed || video.paused || video.ended || videoFrameCallbackId !== null || animationFrameId !== null) return;
        const requestVideoFrameCallback = video.requestVideoFrameCallback?.bind(video);

        if (requestVideoFrameCallback) {
          videoFrameCallbackId = requestVideoFrameCallback(() => {
            videoFrameCallbackId = null;
            drawVideoFrame();
            if (!video.paused && !video.ended) {
              scheduleDraw();
            }
          });
          return;
        }

        const drawOnAnimationFrame = () => {
          drawVideoFrame();
          if (!video.paused && !video.ended) {
            animationFrameId = window.requestAnimationFrame(drawOnAnimationFrame);
          } else {
            animationFrameId = null;
          }
        };

        animationFrameId = window.requestAnimationFrame(drawOnAnimationFrame);
      };

      const cancelScheduledDraw = () => {
        const cancelVideoFrameCallback = video.cancelVideoFrameCallback?.bind(video);

        if (videoFrameCallbackId !== null && cancelVideoFrameCallback) {
          cancelVideoFrameCallback(videoFrameCallbackId);
          videoFrameCallbackId = null;
        }

        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      };

      drawVideoFrame();
      rememberFirstFrame(imageUrl, frameCanvas);
      video.addEventListener('loadeddata', drawVideoFrame);
      video.addEventListener('seeked', drawVideoFrame);
      video.addEventListener('playing', scheduleDraw);
      video.addEventListener('canplay', scheduleDraw);

      attachTimelineControls(timelineState?.current || null);
      if (!attachedEngine) {
        attachTimelineInterval = window.setInterval(() => {
          attachTimelineControls(timelineState?.current || null);
          if (attachedEngine && attachTimelineInterval) {
            window.clearInterval(attachTimelineInterval);
            attachTimelineInterval = undefined;
          }
        }, 100);
      }

      sprite = new Sprite(new Texture({ source: canvasSource }));
      (
        sprite as Sprite & { __timelineVideoCleanup?: () => void }
      ).__timelineVideoCleanup = cleanupVideo = () => {
        if (disposed) return;
        disposed = true;
        cancelScheduledDraw();
        pauseVideo();
        video.removeEventListener('error', handleVideoError);
        video.removeEventListener('loadeddata', drawVideoFrame);
        video.removeEventListener('seeked', drawVideoFrame);
        video.removeEventListener('playing', scheduleDraw);
        video.removeEventListener('canplay', scheduleDraw);
        if (attachTimelineInterval) {
          window.clearInterval(attachTimelineInterval);
        }
        if (attachedEngine?.listener?.off) {
          attachedEngine.listener.off('play', playVideo);
          attachedEngine.listener.off('paused', pauseVideo);
          attachedEngine.listener.off('afterSetTime', handleTimeUpdate);
          attachedEngine.listener.off('afterSetPlayRate', handleRateChange);
        }
        canvasSource.destroy();
        releaseVideo(imageUrl, video);
      };
    } else {
      const PIXI = await import('pixi.js');
      await PIXI.Assets.load(imageUrl);
      sprite = PIXI.Sprite.from(imageUrl);
    }
    // Staging (addChild) and ref bookkeeping are the caller's job so it can
    // swap only once the first frame is ready (see ImageRenderer).
    sprite.anchor.set(0.5);
    sprite.position.set(width / 2, height / 2);

    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    sprite.interactive = false;

    let isDragging = false;
    let dragData: any = null;

    sprite.on('pointerdown', (event) => {
      isDragging = true;
      dragData = event.data;
      sprite.alpha = 0.8;
    });

    sprite.on('pointermove', () => {
      if (isDragging && dragData) {
        const newPosition = dragData.getLocalPosition(sprite.parent);
        sprite.position.x = newPosition.x;
        sprite.position.y = newPosition.y;
      }
    });

    sprite.on('pointerup', () => {
      isDragging = false;
      dragData = null;
      sprite.alpha = 1;
    });

    sprite.on('pointerupoutside', () => {
      isDragging = false;
      dragData = null;
      sprite.alpha = 1;
    });

    const scale = Math.max(width / sprite.width, height / sprite.height);
    sprite.scale.set(scale);

    return sprite;
  } catch (error) {
    cleanupVideo?.();
    if (!signal?.aborted) console.error('Error loading image:', error);
  }
};
